import { NeoNamespace } from '../common-enum';
import { NeoError } from './neo-error';

export interface RetryOptions {
  times?: number;
  interval?: number;
  validator?: (args: any) => boolean;
}

export interface RetryStatus {
  times: number;
  delay: number;
}

/**
 * 重试执行函数
 * @param callback 需要重试执行的函数，函数返回值必须是 Promise 对象
 * @param options 重试选项
 */
export function retry<T>(
  callback: (retryStatus?: RetryStatus) => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = {
    times: 3,
    interval: 0,
    validator: () => true,
    ...options,
  };
  if (opts.times < 0) {
    opts.times = 0;
  }
  if (opts.interval < 0) {
    opts.interval = 0;
  }
  // 执行次数
  let times = 0;
  const startTime = Date.now();

  // 重试动作
  function runTask() {
    times += 1;
    const retryStatus: RetryStatus = {
      times,
      // 距离首次调用的延迟时间
      delay: Date.now() - startTime,
    };

    return callback(retryStatus).then((data: any) => {
      // 验证返回结果
      if (opts.validator(data)) {
        return Promise.resolve(data);
      }
      return Promise.reject(
        new NeoError(NeoNamespace.UtilControlFlow, 'retry validation failed')
      );
    });
  }

  return new Promise((resolve, reject) => {
    // 定义循环
    function loop() {
      runTask()
        .then(resolve)
        .catch((err) => {
          // 终止判定
          // 这里会收到调用异常和执行结果验证未通过2种异常
          if (times < opts.times) {
            window.setTimeout(loop, opts.interval);
          } else {
            reject(err);
          }
        });
    }
    // 启动循环
    loop();
  });
}

/**
 * 休眠指定时间，通过 setTimeout 实现
 * @param time 延迟时间，单位：ms
 */
export function sleep(time: number = 0): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve();
    }, time);
  });
}
